<template>
    <section class="product-structure-builder">
        <ProductComponentModal
            ref="componentModal"
            :search-products="searchProducts"
            @submit="submitComponent"
        />

        <div class="product-structure-builder__header">
            <div>
                <h5 class="mb-25">
                    {{ translate(
                        'Page(ReceivedRequest):Component(ProductStructure):title',
                        'Build product structure'
                    ) }}
                </h5>
                <p class="mb-0 text-less-muted">
                    {{ translate(
                        'Page(ReceivedRequest):Component(ProductStructure):description',
                        'Add the components or materials for which you need information from another supplier.'
                    ) }}
                </p>
            </div>

            <b-form-select
                v-if="showModeSelector"
                class="product-structure-builder__mode"
                :value="mode"
                :options="modeOptions"
                @input="$emit('mode-change', $event)"
            />
        </div>

        <div v-if="loading" class="product-structure-builder__loading">
            <b-spinner small variant="primary" />
        </div>
        <ProductStructureTree
            v-else-if="hierarchy && hierarchy.id"
            :hierarchy="hierarchy"
            :busy-node-key="busyNodeKey"
            @add="openAddModal"
            @edit="openEditModal"
            @remove="removeComponent"
        />
    </section>
</template>

<script>
import { BFormSelect, BSpinner } from 'bootstrap-vue'
import ProductComponentModal from './ProductComponentModal.vue'
import ProductStructureTree from './ProductStructureTree.vue'

export default {
    name: 'ProductStructureBuilder',
    components: {
        BFormSelect,
        BSpinner,
        ProductComponentModal,
        ProductStructureTree,
    },
    props: {
        hierarchy: { type: Object, required: true },
        mode: { type: String, default: 'top-down' },
        showModeSelector: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        busyNodeKey: { type: String, default: null },
        searchProducts: { type: Function, required: true },
        loadComponentDetails: { type: Function, required: true },
    },
    data() {
        return {
            modeOptions: [
                { value: 'top-down', text: 'Top down' },
                { value: 'bottom-up', text: 'Bottom up' },
            ],
        }
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        openAddModal(entry) {
            this.$refs.componentModal.showAdd(entry.node, entry.key)
        },
        async openEditModal(entry) {
            try {
                const details = await this.loadComponentDetails(entry.node)
                this.$refs.componentModal.showEdit(
                    entry.node,
                    entry.parent,
                    entry.key,
                    details,
                )
            } catch (error) {
                this.$emit('error', error)
            }
        },
        submitComponent(payload) {
            this.$emit(payload.operation === 'add' ? 'add-component' : 'update-component', {
                ...payload,
                mode: this.mode,
            })
        },
        async removeComponent(entry) {
            const result = await this.$swal({
                title: this.translate(
                    'Page(ReceivedRequest):Component(ProductStructure):remove-title',
                    'Remove component?',
                ),
                text: this.translate(
                    'Page(ReceivedRequest):Component(ProductStructure):remove-confirmation',
                    `Remove “${entry.node.title}” from this product structure?`,
                ),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.translate('Component(Button):remove', 'Remove'),
                cancelButtonText: this.translate('Component(Button):cancel', 'Cancel'),
                customClass: {
                    confirmButton: 'btn btn-danger',
                    cancelButton: 'btn btn-outline-secondary ml-1',
                },
                buttonsStyling: false,
            })

            if (result.isConfirmed) {
                this.$emit('remove-component', {
                    nodeKey: entry.key,
                    parentId: entry.parent.id,
                    componentId: entry.node.id,
                })
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.product-structure-builder {
    margin-top: 1.25rem;
    padding: 1rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.product-structure-builder__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: .85rem;
}

.product-structure-builder__mode {
    width: 10rem;
    margin-left: 1rem;
}

.product-structure-builder__loading {
    display: flex;
    min-height: 5rem;
    align-items: center;
    justify-content: center;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}
</style>
